import type { FeatureCollection } from 'geojson';
import type { GeoJSONSource, LayerSpecification } from 'maplibre-gl';
import { useEffect, useId } from 'react';
import { useMap } from './map-view';

type Paint = Omit<LayerSpecification, 'id' | 'source'>;

/**
 * Read-only GeoJSON overlay (context such as lines, stations, zones). Each entry of `layers`
 * becomes a MapLibre layer on the same source.
 */
export function GeoJsonLayer({
  data,
  layers,
  onClick,
  onHover,
  dim = 1,
}: {
  data: FeatureCollection;
  layers: Paint[];
  /** Fades the layers (1 = as drawn, 0.25 = a quarter of their opacity), e.g. behind a selection. */
  dim?: number;
  onClick?: (props: Record<string, unknown>) => void;
  /** Properties of the feature under the pointer; null when it leaves. */
  onHover?: (props: Record<string, unknown> | null) => void;
}) {
  const map = useMap();
  const id = `ctx-${useId().replace(/:/g, '')}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: layers are static per usage
  useEffect(() => {
    if (!map) return;
    map.addSource(id, { type: 'geojson', data });
    // Text labels need font glyphs, which the offline fallback style does not have (icons do not).
    const hasGlyphs = !!map.getStyle().glyphs;
    layers.forEach((layer, i) => {
      if (layer.type === 'symbol' && !hasGlyphs && layer.layout && 'text-field' in layer.layout) return;
      map.addLayer({ ...layer, id: `${id}-${i}`, source: id } as LayerSpecification);
    });
    const click = (e: maplibregl.MapLayerMouseEvent) =>
      onClick?.((e.features?.[0]?.properties ?? {}) as Record<string, unknown>);
    const enter = () => {
      if (onClick) map.getCanvas().style.cursor = 'pointer';
    };
    const move = (e: maplibregl.MapLayerMouseEvent) =>
      onHover?.((e.features?.[0]?.properties ?? null) as Record<string, unknown> | null);
    const leave = () => {
      map.getCanvas().style.cursor = '';
      onHover?.(null);
    };
    if (onClick || onHover) {
      layers.forEach((_, i) => {
        if (!map.getLayer(`${id}-${i}`)) return;
        map.on('click', `${id}-${i}`, click);
        map.on('mousemove', `${id}-${i}`, move);
        map.on('mouseenter', `${id}-${i}`, enter);
        map.on('mouseleave', `${id}-${i}`, leave);
      });
    }
    return () => {
      if (!map.getStyle()) return;
      layers.forEach((_, i) => {
        map.off('click', `${id}-${i}`, click);
        map.off('mousemove', `${id}-${i}`, move);
        map.off('mouseenter', `${id}-${i}`, enter);
        map.off('mouseleave', `${id}-${i}`, leave);
        if (map.getLayer(`${id}-${i}`)) map.removeLayer(`${id}-${i}`);
      });
      if (map.getSource(id)) map.removeSource(id);
    };
  }, [map, id]);

  useEffect(() => {
    (map?.getSource(id) as GeoJSONSource | undefined)?.setData(data);
  }, [map, id, data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: layers are static per usage
  useEffect(() => {
    if (!map?.getStyle()) return;
    layers.forEach((layer, i) => {
      if (!map.getLayer(`${id}-${i}`)) return;
      for (const prop of OPACITY[layer.type] ?? []) {
        const base = (layer.paint as Record<string, unknown> | undefined)?.[prop] ?? 1;
        const value = dim === 1 ? base : typeof base === 'number' ? base * dim : ['*', base, dim];
        map.setPaintProperty(`${id}-${i}`, prop, value);
      }
    });
  }, [map, id, dim]);

  return null;
}

/** Paint properties that make up a layer's opacity, by layer type. */
const OPACITY: Record<string, string[]> = {
  line: ['line-opacity'],
  circle: ['circle-opacity', 'circle-stroke-opacity'],
  symbol: ['icon-opacity', 'text-opacity'],
  fill: ['fill-opacity'],
};

export const fc = (features: FeatureCollection['features']): FeatureCollection => ({
  type: 'FeatureCollection',
  features,
});

export const lineLayer = (color: string | unknown[] = ['get', 'color'], width = 4, opacity = 0.8): Paint => ({
  type: 'line',
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: { 'line-color': color as string, 'line-width': width, 'line-opacity': opacity },
});
export const fillLayer = (color: string | unknown[] = ['get', 'color'], opacity = 0.2): Paint => ({
  type: 'fill',
  paint: { 'fill-color': color as string, 'fill-opacity': opacity, 'fill-outline-color': color as string },
});
export const labelLayer = (field = 'name'): Paint => ({
  type: 'symbol',
  layout: { 'text-field': ['get', field], 'text-size': 11, 'text-offset': [0, 1.2], 'text-anchor': 'top' },
  paint: { 'text-color': '#334155', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
});

/** Chevrons along a line in its direction of travel (coordinates must be in travel order). */
export const arrowLayer = (icon: string): Paint => ({
  type: 'symbol',
  layout: {
    'symbol-placement': 'line',
    'symbol-spacing': 90,
    'icon-image': icon,
    'icon-size': 0.9,
    'icon-allow-overlap': true,
    'icon-rotation-alignment': 'map',
  },
});
